import { useEffect, useRef, useState } from 'react';
import {
  DocumentRequirement,
  ProfessionalDocument,
  uploadFile,
  type UploadPurpose,
} from '../../lib/professional-api';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';
import { ThemedDatePicker } from './ThemedDatePicker';

const STATUS_CHIP: Record<string, string> = {
  PENDING: 'bg-[#fffbeb] text-[#92400e]',
  APPROVED: 'bg-[#ecfdf5] text-[#065f46]',
  REJECTED: 'bg-[#fef2f2] text-[#991b1b]',
};

/**
 * Local "draft" of a document the customer is staging — bytes have
 * been pushed to S3 already (so we can preview) but the DB save
 * hasn't happened yet. The parent batches every requirement's
 * pending row into a single submit at the bottom of the page.
 */
export type PendingDoc = {
  front_file_url: string | null;
  back_file_url: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  original_filename: string | null;
  expiry_date: string | null;
};

export function emptyPending(): PendingDoc {
  return {
    front_file_url: null,
    back_file_url: null,
    mime_type: null,
    file_size_bytes: null,
    original_filename: null,
    expiry_date: null,
  };
}

export function pendingFromServer(doc: ProfessionalDocument): PendingDoc {
  return {
    front_file_url: doc.front_file_url,
    back_file_url: doc.back_file_url,
    mime_type: doc.mime_type,
    file_size_bytes: doc.file_size_bytes,
    original_filename: doc.original_filename,
    expiry_date: doc.expiry_date,
  };
}

function purposeFor(requirement: DocumentRequirement): UploadPurpose {
  const videoOnly =
    requirement.accepts.length > 0 &&
    requirement.accepts.every((m) => m.startsWith('video/'));
  return videoOnly ? 'PROFESSIONAL_VIDEO' : 'PROFESSIONAL_DOCUMENT';
}

type Side = 'front' | 'back';

type Props = {
  requirement: DocumentRequirement;
  /** Persisted document row from the backend (review state, etc.).
   *  Read-only — actual edits live in `pending`. */
  document: ProfessionalDocument | null;
  /** Local draft for this requirement. Parent owns it. */
  pending: PendingDoc;
  onChange: (patch: Partial<PendingDoc>) => void;
  /** Validation error from the parent's submit (shown inline). */
  submitError?: string | null;
};

/**
 * Renders one document requirement card: front (and optional back)
 * upload buttons, an expiry date picker when needed, current review
 * status, and rejection notes if there are any.
 *
 * File bytes are pushed to S3 the moment the user picks them so we
 * can show a preview, but the DB save (`POST /professional/me/documents`)
 * happens up at the page level when the customer clicks the Continue
 * button. That avoids the "front uploaded → backend rejects because
 * back is missing → row half-saved" race we used to hit on
 * front+back requirements.
 */
export function DocumentUploader({
  requirement,
  document,
  pending,
  onChange,
  submitError,
}: Props) {
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<Side | null>(null);
  // Blob-URL previews for files picked this session. We keep them
  // separate from `pending.*_file_url` (the S3 URL the backend
  // returns) because S3 buckets are often private and the returned
  // URL won't render in <img>. The S3 URL is what gets POSTed to
  // /professional/me/documents; this blob is for instant preview.
  const [localFrontPreview, setLocalFrontPreview] = useState<string | null>(
    null,
  );
  const [localBackPreview, setLocalBackPreview] = useState<string | null>(
    null,
  );

  // Revoke blob URLs on unmount / replacement to avoid the memory leak
  // browsers warn about — they're not garbage-collected automatically.
  useEffect(() => {
    return () => {
      if (localFrontPreview) URL.revokeObjectURL(localFrontPreview);
      if (localBackPreview) URL.revokeObjectURL(localBackPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptAttr = requirement.accepts.join(',');
  const purpose = purposeFor(requirement);

  // Front-only docs hide the back column. We still let the customer
  // upload the front again to replace it.
  const hasBack = requirement.has_front_back;

  const expiry = pending.expiry_date
    ? new Date(pending.expiry_date).toISOString().slice(0, 10)
    : '';
  const expired =
    pending.expiry_date &&
    new Date(pending.expiry_date).getTime() < Date.now();

  const uploadSide = async (side: Side, file: File) => {
    if (!requirement.accepts.includes(file.type)) {
      toast.error(
        `Unsupported file type. Allowed: ${requirement.accepts
          .map((m) => m.split('/').pop())
          .join(', ')}.`,
      );
      return;
    }
    setUploading(side);
    // Show the preview instantly from the picked file. The S3 upload
    // can take a few seconds; we don't want the card to look broken
    // in the meantime.
    const blobUrl = URL.createObjectURL(file);
    if (side === 'front') {
      if (localFrontPreview) URL.revokeObjectURL(localFrontPreview);
      setLocalFrontPreview(blobUrl);
    } else {
      if (localBackPreview) URL.revokeObjectURL(localBackPreview);
      setLocalBackPreview(blobUrl);
    }
    try {
      const up = await uploadFile(file, purpose);
      // Push the S3 URL into the parent's pending draft. The DB save
      // happens later when the customer clicks Continue.
      onChange(
        side === 'front'
          ? {
              front_file_url: up.url,
              mime_type: file.type,
              file_size_bytes: file.size,
              original_filename: file.name,
            }
          : {
              back_file_url: up.url,
              // Front side metadata stays whatever was there before;
              // we only stamp it from the front pick.
            },
      );
    } catch (err) {
      // S3 upload failed — drop the optimistic preview so the user
      // doesn't think the file was accepted.
      if (side === 'front') {
        URL.revokeObjectURL(blobUrl);
        setLocalFrontPreview(null);
      } else {
        URL.revokeObjectURL(blobUrl);
        setLocalBackPreview(null);
      }
      toast.error(apiErrorMessage(err, 'Upload failed.'));
    } finally {
      setUploading(null);
    }
  };

  const setExpiry = (value: string) => {
    onChange({ expiry_date: value || null });
  };

  // Once admin marks the doc APPROVED the pro can't replace it — the
  // backend rejects the upload anyway, but we also hide the Replace
  // button + the expiry editor so it's not misleading.
  const isApproved = document?.review_status === 'APPROVED';

  const SideCard = ({
    side,
    fileUrl,
  }: {
    side: Side;
    fileUrl: string | null;
  }) => {
    const inputRef = side === 'front' ? frontInputRef : backInputRef;
    const label = side === 'front' ? (hasBack ? 'Front' : 'File') : 'Back';
    const isUploadingThis = uploading === side;

    return (
      <div className="flex-1 flex flex-col gap-2 rounded-lg border border-[#eee] bg-white p-3">
        <div className="text-xs font-bold text-[#3a3335]">{label}</div>
        {fileUrl ? (
          fileUrl.match(/\.(mp4|webm|mov|quicktime)$/i) ||
          pending.mime_type?.startsWith('video/') ? (
            <video
              src={fileUrl}
              className="h-32 w-full rounded-md bg-black object-cover"
              controls
              muted
            />
          ) : fileUrl.match(/\.(pdf)$/i) ||
            pending.mime_type === 'application/pdf' ? (
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="h-32 w-full rounded-md bg-[#f6f1f1] flex flex-col items-center justify-center text-[#8e2a2f] hover:bg-[#f0e6e6]"
            >
              <span className="material-icons text-[40px]">
                {''}
              </span>
              <span className="text-xs font-bold">View PDF</span>
            </a>
          ) : (
            <img
              src={fileUrl}
              alt={`${label} preview`}
              className="h-32 w-full rounded-md object-cover"
            />
          )
        ) : (
          <div className="h-32 w-full rounded-md bg-[#f9f9f9] flex items-center justify-center text-[#908e8e] text-xs">
            No file yet
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) uploadSide(side, f);
          }}
        />
        {isApproved ? (
          <div className="w-full h-9 rounded-md bg-[#ecfdf5] text-[#065f46] text-xs font-bold flex items-center justify-center gap-1">
            <span className="material-icons text-[14px]">verified</span>
            Approved — locked
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={isUploadingThis}
            className="w-full h-9 rounded-md bg-[#8e2a2f] text-white text-xs font-bold hover:bg-[#7a2428] disabled:opacity-60"
          >
            {isUploadingThis
              ? 'Uploading…'
              : fileUrl
              ? 'Replace'
              : `Upload ${label.toLowerCase()}`}
          </button>
        )}
      </div>
    );
  };

  // Status chip prefers the server-side review state when there is
  // one; otherwise we show whether a draft is locally staged.
  const localStaged =
    pending.front_file_url ||
    pending.back_file_url ||
    pending.expiry_date;
  const chipLabel = document
    ? document.review_status
    : localStaged
      ? 'Ready to submit'
      : 'Not uploaded';
  const chipClass = document
    ? STATUS_CHIP[document.review_status] ?? 'bg-[#f1f5f9] text-[#475569]'
    : localStaged
      ? 'bg-[#eff6ff] text-[#1d4ed8]'
      : 'bg-[#f1f5f9] text-[#475569]';

  return (
    <div className="w-full rounded-xl border border-[#ede8e9] bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px]">
              {requirement.name}
            </h3>
            {requirement.is_required ? (
              <span className="rounded-full bg-[#fef2f2] text-[#991b1b] px-2 py-0.5 text-[10px] font-bold">
                Required
              </span>
            ) : (
              <span className="rounded-full bg-[#f1f5f9] text-[#475569] px-2 py-0.5 text-[10px] font-bold">
                Optional
              </span>
            )}
            {requirement.has_front_back ? (
              <span className="rounded-full bg-[#fffbeb] text-[#92400e] px-2 py-0.5 text-[10px] font-bold">
                Front + back
              </span>
            ) : null}
            {requirement.has_expiry ? (
              <span className="rounded-full bg-[#eff6ff] text-[#1d4ed8] px-2 py-0.5 text-[10px] font-bold">
                Expires
              </span>
            ) : null}
          </div>
          {requirement.description ? (
            <p className="mt-1 text-[#908e8e] text-[13px] leading-[18px]">
              {requirement.description}
            </p>
          ) : null}
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${chipClass}`}
        >
          {chipLabel}
        </span>
      </div>

      {document?.review_status === 'REJECTED' && document.review_notes ? (
        <div className="rounded-md bg-[#fef2f2] border border-[#fecaca] text-[#991b1b] text-[13px] px-3 py-2 mb-3">
          <span className="font-bold">Reviewer note:</span> {document.review_notes}
        </div>
      ) : null}

      <div className="flex flex-col md:flex-row gap-3">
        <SideCard
          side="front"
          fileUrl={localFrontPreview ?? pending.front_file_url}
        />
        {hasBack ? (
          <SideCard
            side="back"
            fileUrl={localBackPreview ?? pending.back_file_url}
          />
        ) : null}
      </div>

      {requirement.has_expiry ? (
        <div className="mt-3">
          <label className="text-xs font-bold text-[#3a3335] block mb-1">
            Expiry date{' '}
            <span className="text-[#dc2626]" title="Required">
              *
            </span>
          </label>
          <ThemedDatePicker
            value={expiry}
            onChange={setExpiry}
            disabled={isApproved || !pending.front_file_url}
            invalid={
              !!pending.front_file_url && !pending.expiry_date
            }
            className="w-48"
          />
          {expired ? (
            <p className="mt-1 text-[12px] text-[#991b1b]">
              This document has expired.
            </p>
          ) : null}
          {pending.front_file_url && !pending.expiry_date && !expired ? (
            <p className="mt-1 text-[12px] text-[#991b1b] font-bold">
              Pick an expiry date to continue — this field is required.
            </p>
          ) : null}
        </div>
      ) : null}

      {document?.uploaded_at ? (
        <p className="mt-3 text-[12px] text-[#908e8e]">
          Last uploaded {new Date(document.uploaded_at).toLocaleString()}
        </p>
      ) : null}

      {submitError ? (
        <p className="mt-3 text-[12px] text-[#991b1b]">
          {submitError}
        </p>
      ) : null}
    </div>
  );
}
